import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  Button,
  Modal,
  message,
  Divider,
  Row,
  Col,
  Input,
  DatePicker,
  Alert,
} from "antd";
import { Api_Url } from "../../../config/config";
import moment from "moment";
import dayjs from "dayjs";

export const FileStorageModalAdd = ({ onClose, onSuccess, record }) => {
  const [form] = Form.useForm();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);

  const isEditMode = !!record;
  const { Option } = Select;

  const fetchBranches = async () => {
    try {
      const token = JSON.parse(sessionStorage.getItem("user")).accessToken;
      const response = await fetch(
        `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      setBranches(data.data || []);
    } catch (error) {
      message.error("Failed to load branches");
    }
  };

  const parseDateSafe = (dateStr) => {
    if (!dateStr) return null;
    const d = dayjs(dateStr.split("T")[0], "YYYY-MM-DD");
    return d.isValid() ? d : null;
  };

  const populateEditForm = (rec) => {
    form.setFieldsValue({
      CustomerId: rec.customerId,
      LoanNumberSearch: rec.loanNumber,
      PODNumber: rec.podNumber,
      DocumentType: rec.documentType || "Main File",
      PDDDocumentName: rec.pddDocumentName || "",
      CourierCompanyName: rec.courierCompanyName || "",
      BranchRemarks: rec.branchRemarks || "",
      ChequeHandoverDate: parseDateSafe(rec.chequeHandoverDate),
      FileSentDate: parseDateSafe(rec.fileSentDate),
      PODDate: parseDateSafe(rec.podDate),
    });
  };

  useEffect(() => {
    fetchBranches();
    if (isEditMode && record) {
      setCustomerDetails({
        name: record.name,
        applicationNo: record.applicationNo,
        firstDisbursalDate: record.firstDisbursalDate,
      });
      populateEditForm(record);
    }
  }, [isEditMode, record, form]);

  const searchByLoanNumber = async () => {
    const branchId = form.getFieldValue("BranchId");
    const loanNumber = form.getFieldValue("LoanNumberSearch");

    if (!branchId) {
      message.warning("Please select a branch first.");
      return;
    }
    if (!loanNumber) {
      message.warning("Please enter a Loan Number.");
      return;
    }

    setSearching(true);
    try {
      const token = JSON.parse(sessionStorage.getItem("user")).accessToken;
      const response = await fetch(
        `${Api_Url}api/v1/Customer/bybranchidandnameandloannumber/${branchId}/_/?loanNumber=${encodeURIComponent(loanNumber)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = await response.json();

      if (response.ok && result.data?.length > 0) {
        const customer = result.data.filter(
          (customer) => customer.isCoApplicant === false
        )[0];
        setCustomerDetails({
          name: customer.name,
          applicationNo: customer.applicationNo,
          firstDisbursalDate: customer.firstDisbursalDate,
        });
        form.setFieldsValue({ CustomerId: customer.id });
        message.success("Customer found!");
      } else {
        message.error("Customer not found in this branch.");
      }
    } catch (error) {
      message.error("Error searching for customer.");
    } finally {
      setSearching(false);
    }
  };

  const refetchCustomerDetails = async () => {
    const loanNumber = record?.loanNumber;
    if (!loanNumber) {
      message.warning("No loan number available to refetch.");
      return;
    }

    setSearching(true);
    try {
      const token = JSON.parse(sessionStorage.getItem("user")).accessToken;
      // Use the same endpoint as the add flow — search by loan number across all branches
      // branchId is not available on the record, so we search with a wildcard branch approach
      // by iterating branches until we find a match
      const branchesRes = await fetch(
        `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const branchesData = await branchesRes.json();
      const branchList = branchesData.data || [];

      // Find the branch that matches the record's branchName
      const matchedBranch = branchList.find(
        (b) => b.name?.toLowerCase() === record?.branchName?.toLowerCase(),
      );

      if (!matchedBranch) {
        message.warning("Could not identify branch to refetch customer.");
        setSearching(false);
        return;
      }

      const response = await fetch(
        `${Api_Url}api/v1/Customer/bybranchidandnameandloannumber/${matchedBranch.id}/_/?loanNumber=${encodeURIComponent(loanNumber)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = await response.json();

      if (response.ok && result.data?.length > 0) {
        const customer =
          result.data.find((c) => c.isCoApplicant === false) || result.data[0];
        setCustomerDetails({
          name: customer.name,
          applicationNo: customer.applicationNo,
          firstDisbursalDate: customer.firstDisbursalDate,
        });
        form.setFields([{ name: "CustomerId", value: customer.id }]);
        message.success("Customer details refreshed!");
      } else {
        message.warning("Could not find updated customer details.");
      }
    } catch (error) {
      message.error("Error fetching customer details.");
    } finally {
      setSearching(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const token = JSON.parse(sessionStorage.getItem("user")).accessToken;

      const payload = {
        ...(isEditMode && { Id: record.fileMasterId }),
        CustomerId: values.CustomerId,
        DocumentType: values.DocumentType,
        PDDDocumentName: values.PDDDocumentName, 
        CourierCompanyName: values.CourierCompanyName, 
        BranchRemarks: values.BranchRemarks, 
        ChequeHandoverDate: values.ChequeHandoverDate?.format("YYYY-MM-DD"),
        FileSentDate: values.FileSentDate?.format("YYYY-MM-DD"),
        PODDate: values.PODDate?.format("YYYY-MM-DD"),
        PODNumber: values.PODNumber,
      };

      const method = isEditMode ? "PUT" : "POST";
      const url = isEditMode
        ? `${Api_Url}api/v1/FileMaster/${record.fileMasterId}`
        : `${Api_Url}api/v1/FileMaster`;

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.succeeded) {
        message.success(
          `Details ${isEditMode ? "updated" : "submitted"} successfully!`,
        );
        if (onSuccess) onSuccess();
        onClose();
      } else {
        throw new Error(result.message || "Operation failed");
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <b>{isEditMode ? "Edit File Handover" : "Add File Handover Record"}</b>
      }
      width={800}
      open={true}
      onCancel={onClose}
      centered
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
          disabled={!customerDetails}
        >
          {isEditMode ? "Update Details" : "Submit Handover"}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ DocumentType: "Main File" }}
      >
        {!isEditMode && (
          <>
            <Divider orientation="left">
              Step 1: Select Branch & Find Customer
            </Divider>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="BranchId"
                  label="Branch"
                  rules={[{ required: true, message: "Select a branch" }]}
                >
                  <Select
                    placeholder="Select branch"
                    showSearch
                    optionFilterProp="children"
                    onChange={() => {
                      setCustomerDetails(null);
                      form.setFieldsValue({ LoanNumberSearch: "" });
                    }}
                  >
                    {branches.map((b) => (
                      <Option key={b.id} value={b.id}>
                        {b.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={20}>
                <Form.Item
                  name="LoanNumberSearch"
                  label="Search by Loan Number"
                  rules={[{ required: true, message: "Search by Loan Number" }]}
                >
                  <Input
                    placeholder="RHFL..."
                    size="large"
                    onPressEnter={searchByLoanNumber}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Button
                  type="primary"
                  size="large"
                  onClick={searchByLoanNumber}
                  loading={searching}
                  style={{ marginTop: "30px", width: "100%" }}
                >
                  Search
                </Button>
              </Col>
            </Row>
          </>
        )}

        {customerDetails && (
          <>
            <Alert
              message={
                <Row justify="space-between" align="middle">
                  <Col>
                    {isEditMode ? "Customer Information" : "Customer Identified"}
                  </Col>
                  {isEditMode && (
                    <Col>
                      <Button
                        size="small"
                        icon={<span style={{ marginRight: 4 }}>↻</span>}
                        loading={searching}
                        disabled={searching}
                        onClick={refetchCustomerDetails}
                        style={{ fontSize: "12px" }}
                      >
                        Refetch Customer Details
                      </Button>
                    </Col>
                  )}
                </Row>
              }
              description={
                <Row>
                  <Col span={12}>
                    <b>Name:</b> {customerDetails.name}
                  </Col>
                  <Col span={12}>
                    <b>Application No:</b> {customerDetails.applicationNo}
                  </Col>
                  <Col span={24} style={{ marginTop: "5px" }}>
                    <b>First Disbursal Date:</b>{" "}
                    {customerDetails.firstDisbursalDate
                      ? dayjs(customerDetails.firstDisbursalDate).format(
                          "DD-MM-YYYY",
                        )
                      : "N/A"}
                  </Col>
                </Row>
              }
              type={isEditMode ? "info" : "success"}
              showIcon
              style={{ marginBottom: "20px" }}
            />

            <Form.Item name="CustomerId" hidden>
              <Input />
            </Form.Item>

            <Divider orientation="left">
              {isEditMode
                ? "Update Handover Details"
                : "Step 2: Handover Details"}
            </Divider>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="DocumentType"
                  label="Document Category"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Select size="large">
                    <Option value="Main File">Main File (Full Set)</Option>
                    <Option value="PDD">PDD Documents (Post Disbursal)</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.DocumentType !== currentValues.DocumentType
                  }
                >
                  {({ getFieldValue }) => (
                    <Form.Item
                      name="PDDDocumentName"
                      label="PDD Document Name"
                      rules={[
                        {
                          required: getFieldValue("DocumentType") === "PDD",
                          message: "Please enter the PDD document name",
                        },
                      ]}
                    >
                      <Input
                        placeholder="e.g. Sale Deed, RC, etc."
                        disabled={getFieldValue("DocumentType") !== "PDD"}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="CourierCompanyName"
                  label="Courier Company Name"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input placeholder="e.g. BlueDart, DTDC" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="ChequeHandoverDate"
                  label="Cheque Handover Date"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    inputReadOnly={false}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="FileSentDate"
                  label="File Sent to Central Ops Date"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    inputReadOnly={false}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="PODNumber"
                  label="Courier POD Number"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input placeholder="Enter Tracking ID" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="PODDate"
                  label="Courier POD Date"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    inputReadOnly={false}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="BranchRemarks" label="Branch Remarks">
                  <Input.TextArea
                    rows={2}
                    placeholder="Any additional notes..."
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Modal>
  );
};
